import { refreshAppearance } from "./appearance";
/**
 * The console.
 *
 * A sidebar of destinations, a topbar saying where you are, and one region
 * that is redrawn whenever anything it shows changes. The building arrives
 * over live listeners and every write goes to a callable.
 *
 * Firestore holds the state and the view is one word. The address bar holds
 * that word too (app.liyumanagement.com/pay), so a screen can be linked to, reloaded
 * and gone back from; routes.ts says which words are screens.
 */

import { type FeatureId, newestSent, offers, unreadChat, unreadLabel } from "@liyu/core";
import {
  checkChapaPayment,
  checkTelebirrPayment,
  organizationStatus,
  unfinishedChapaOrders,
} from "./data";
import { captureDrafts, restoreDrafts } from "./drafts";
import { el, esc, stagger, toast, translate, wireReceiptViewer } from "./dom";
import { currentSession, onSignedOut, signOut } from "./session";
import type { Session } from "./session";
import { followChapaOrder, followChat, followTelebirrOrder, has, store, watch } from "./store";
import { chatSeenAt, markChatSeen, openedChat } from "./chat";
import {
  money,
  skeleton,
  skeletonRows,
  skeletonStats,
  skeletonTable,
  skeletonTitle,
} from "./pieces";
import { accountant, committee, resident } from "./views";
import type { View } from "./views";
import { language, other, otherLanguage, setLanguage, t } from "./strings";
import { pathFor, screenAt, signInPath } from "./routes";
import { showLoaderIfSlow } from "./loader";
import type { Key } from "./strings";

interface Destination {
  id: string;
  label: Key;
  icon: string;
  /** The feature this destination is, so the contract decides whether it shows. */
  feature: FeatureId;
  /** True on the one destination that says how much is waiting. */
  counted?: boolean;
  /** Shown only where Chapa is on, or once somebody has paid with it. */
  chapa?: boolean;
}

/**
 * The same five per role as the drawing, in the same order.
 *
 * A role with no list of its own falls back to the resident's, which is the
 * only one of the three that shows nothing another role may not see.
 */
const RESIDENT_NAV: Destination[] = [
  { id: "home", label: "nav.home", icon: "⌂", feature: "residentBalance" },
  { id: "pay", label: "nav.pay", icon: "◈", feature: "residentSubmitPayment" },
  { id: "repairs", label: "nav.repairs", icon: "⚒", feature: "residentReportRepair" },
  { id: "notices", label: "nav.notices", icon: "☰", feature: "residentNotices" },
  { id: "chat", label: "nav.chat", icon: "✉", feature: "residentChat" },
  { id: "give", label: "nav.give", icon: "♡", feature: "residentGive" },
];

const NAV: Record<string, Destination[]> = {
  resident: RESIDENT_NAV,
  accountant: [
    { id: "home", label: "nav.queue", icon: "◉", counted: true, feature: "verifyPayments" },
    { id: "units", label: "nav.units", icon: "▦", feature: "accountantUnits" },
    { id: "books", label: "nav.books", icon: "▣", feature: "books" },
    { id: "arrears", label: "nav.arrears", icon: "!", feature: "books" },
    { id: "journal", label: "nav.journal", icon: "≡", feature: "books" },
    { id: "chapa", label: "nav.chapa", icon: "◇", feature: "accountantChapa", chapa: true },
    {
      id: "advertisements",
      label: "nav.advertisements",
      icon: "☎",
      feature: "accountantAdvertisements",
    },
  ],
  committee: [
    { id: "home", label: "nav.overview", icon: "⌂", feature: "overview" },
    { id: "units", label: "nav.units", icon: "▦", feature: "overview" },
    { id: "chapa", label: "nav.chapa", icon: "◇", feature: "committeeChapa", chapa: true },
    { id: "campaigns", label: "nav.campaigns", icon: "◎", feature: "campaigns" },
    { id: "repairs", label: "nav.repairs", icon: "⚒", feature: "seeRepairs" },
    { id: "notices", label: "nav.notices", icon: "☰", feature: "notices" },
    { id: "chat", label: "nav.chat", icon: "✉", feature: "committeeChat" },
    { id: "advertisements", label: "nav.advertisements", icon: "☎", feature: "advertisements" },
    { id: "journal", label: "nav.journal", icon: "≡", feature: "committeeJournal" },
  ],
};

/** The destinations the feature contract says this desktop offers this role. */
function destinations(role: string): Destination[] {
  return (NAV[role] ?? RESIDENT_NAV).filter(
    (item) =>
      offers(item.feature, "desktop", role, store.entitlements) &&
      (!item.chapa || store.chapa || store.chapaReceipts.length > 0),
  );
}

/**
 * What to call the person signed in.
 *
 * Written out rather than built from the claim, so every name the sidebar can
 * print is a key the string tests can see. A role interpolated into a key path
 * is invisible to that check, and the first anybody knows is a sidebar reading
 * "role.platform_admin".
 */
const ROLE_NAME = {
  resident: "role.resident",
  accountant: "role.accountant",
  committee: "role.committee",
  platform_admin: null,
} as const satisfies Record<string, Key | null>;

/**
 * What a screen needs before it can be drawn, and what it looks like waiting.
 *
 * `needs` is the listeners that screen actually reads, not every listener the
 * role opened. The journal reads the change log and nothing else, so it is
 * drawn the moment the change log answers rather than sitting grey until the
 * month's expenses have also arrived.
 *
 * `shape` is built from the same grids and cards the real screen uses, so the
 * figures land in the boxes that are already there instead of pushing them
 * down the page.
 */
interface Shell {
  needs: string[];
  shape: () => string;
}

const STATS_THEN_TABLE = (columns: 2 | 3 | 4, cols: number): (() => string) =>
  function shape(): string {
    return skeletonStats(columns) + skeletonTitle() + skeletonTable(cols);
  };

const SHELLS: Record<string, Record<string, Shell>> = {
  resident: {
    home: {
      needs: ["summaries", "payments", "complaints", "notices", "reminders"],
      shape: () => skeletonStats(3) + skeletonTitle() + skeletonRows(3) + skeletonTitle(),
    },
    pay: { needs: ["summaries", "ledger", "unit"], shape: () => skeletonRows(4) },
    payments: {
      needs: ["payments", "ledger", "extras"],
      shape: () => skeletonRows(4),
    },
    repairs: { needs: ["complaints"], shape: () => skeletonTitle() + skeletonRows(3) },
    notices: { needs: ["notices"], shape: () => skeletonRows(3) },
    give: { needs: ["campaigns"], shape: () => skeletonRows(2) },
    chat: { needs: ["chat", "campaigns"], shape: () => skeletonRows(4) },
  },
  accountant: {
    advertisements: { needs: ["advertisements"], shape: () => skeletonRows(3) },
    home: {
      needs: ["waiting", "held", "summaries", "units", "expenses"],
      shape: STATS_THEN_TABLE(3, 6),
    },
    units: {
      needs: ["units", "summaries", "waiting"],
      shape: () => skeletonTable(4, 8),
    },
    books: {
      needs: ["expenses", "summaries", "units", "held", "accounts"],
      shape: STATS_THEN_TABLE(4, 5),
    },
    arrears: { needs: ["summaries", "units", "waiting"], shape: STATS_THEN_TABLE(3, 5) },
    journal: { needs: ["changes", "people"], shape: () => skeletonRows(6) },
    chapa: { needs: ["chapa"], shape: STATS_THEN_TABLE(3, 6) },
  },
  committee: {
    home: {
      needs: ["summaries", "units", "expenses", "held", "waiting", "complaints"],
      shape: () => skeletonStats(4) + skeletonTitle() + skeletonRows(4),
    },
    units: {
      needs: ["units", "summaries", "waiting"],
      shape: () => skeletonTable(4, 8),
    },
    campaigns: { needs: ["campaigns"], shape: () => skeletonTitle() + skeletonRows(3) },
    advertisements: { needs: ["advertisements"], shape: () => skeletonRows(3) },
    repairs: { needs: ["complaints"], shape: () => skeletonRows(4) },
    notices: { needs: ["notices"], shape: () => skeletonTitle() + skeletonRows(3) },
    chat: { needs: ["chat", "campaigns"], shape: () => skeletonRows(4) },
    journal: { needs: ["changes", "people"], shape: () => skeletonRows(6) },
    chapa: { needs: ["chapa"], shape: STATS_THEN_TABLE(3, 6) },
  },
};

/** The screen being drawn, or a sensible shell for one this table has not met. */
function shellFor(role: string, view: string): Shell {
  if (view === "notifications" || view.startsWith("notification:")) {
    return { needs: ["inbox"], shape: () => skeletonRows(4) };
  }
  if (role === "committee" && view.startsWith("analytics-")) {
    return {
      needs: ["summaries", "units", "expenses"],
      shape: () => skeletonStats(3) + skeletonRows(4),
    };
  }
  return SHELLS[role]?.[view] ?? { needs: [], shape: () => skeletonStats(3) + skeletonRows(3) };
}

let current = "home";
/** Asks Chapa about a resident's unfinished orders when the tab is looked at again. */
let recheckOnView: (() => void) | null = null;
let session: Session | null = null;
/** Ends the building's live reads, which only a signed-in member may hold. */
let stopWatching: (() => void) | null = null;

/**
 * Chapa orders this page has already told the resident were verified. Kept
 * for the page's life, so a later snapshot of the same order does not put the
 * box up again after they have been taken Home.
 */
const toldPaid = new Set<string>();
const LEAVE_AFTER_MS = 3200;

/**
 * The answer, the moment Chapa's payment is on the resident's account.
 *
 * Back from Chapa the pay screen still showed the old figure owed for a
 * moment, because the month's total follows the ledger, and a resident reading
 * "ETB 4,000 owed" right after paying it tries again. So this covers the page
 * with a tick drawn in a box and then takes them Home. It shows only for an
 * order the function moved to paid, which it does only after Chapa confirmed
 * the money and the payment was verified in the books.
 */
function drawPaid(): void {
  const order = store.chapaOrder?.order;
  if (
    !session ||
    session.role !== "resident" ||
    order?.status !== "paid" ||
    toldPaid.has(order.id) ||
    document.getElementById("paidBox")
  ) {
    return;
  }
  const orderId = order.id;
  const overlay = document.createElement("div");
  overlay.className = "paid-overlay";
  overlay.id = "paidBox";
  overlay.setAttribute("role", "alertdialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "paidTitle");
  overlay.setAttribute("aria-describedby", "paidBody");
  overlay.innerHTML =
    `<div class="paid-box">` +
    `<div class="paid-mark" aria-hidden="true"><svg width="52" height="52" viewBox="0 0 52 52"><path d="M12 27 L22 37 L41 17" /></svg></div>` +
    `<h2 id="paidTitle">${esc(t("paid.title"))}</h2>` +
    `<p id="paidBody">${esc(t("paid.body", { amount: money(order.amount) }))}</p>` +
    `<p class="hint">${esc(t("paid.leaving"))}</p>` +
    `<div class="actions"><button type="button" class="btn btn-gold" id="paidHome">${esc(
      t("paid.home"),
    )}</button></div>` +
    `</div>`;

  let leave: number | null = null;
  const done = (): void => {
    if (leave !== null) window.clearTimeout(leave);
    document.removeEventListener("keydown", onKey);
    toldPaid.add(orderId);
    overlay.remove();
    go("home");
  };
  const onKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape") done();
  };
  document.addEventListener("keydown", onKey);
  overlay.querySelector("#paidHome")?.addEventListener("click", done);
  document.body.append(overlay);
  overlay.querySelector<HTMLButtonElement>("#paidHome")?.focus();
  leave = window.setTimeout(done, LEAVE_AFTER_MS);
}

/**
 * Stops reading and drawing, before the login those reads ride on goes away.
 *
 * Signing out with the reads still open had every one of them refused at once,
 * and the refusal was drawn: "Failed. Try again" over the rules' own words,
 * for a moment on the way out, or for good in a second tab left open.
 */
function letGo(): void {
  session = null;
  stopWatching?.();
  stopWatching = null;
  followChat(null);
}

/** Starts the live reads for the person signed in. */
function hold(found: Session): void {
  session = found;
  stopWatching = watch(found, () => draw(false));
}

/*
 * Back from Chapa's checkout with the browser's Back button, Chrome can hand
 * this page back frozen exactly as it was left. Its live listeners do not
 * reliably wake with it, so a payment confirmed in the meantime stayed "due"
 * on screen until the resident refreshed by hand. A page restored that way is
 * loaded afresh instead, which reads the books as they are now.
 */
const RESUME_KEY = "liyu.resumeView";
window.addEventListener("pageshow", (event) => {
  if (!event.persisted) return;
  // Back on the screen they left from, not Home. A convenience only: if the
  // browser will not store it, the reload still shows the right figures.
  try {
    sessionStorage.setItem(RESUME_KEY, current);
  } catch (cause) {
    console.warn("Could not remember the screen across a reload", cause);
  }
  window.location.reload();
});

/**
 * The screen the address names, if this role has one by that name. Read against
 * the role's whole list rather than what is showing, because the entitlements
 * and Chapa settings that hide some of them have not arrived yet.
 */
function linkedView(role: string): string | null {
  const view = screenAt(window.location.pathname);
  if (view === null) return null;
  if (view === "notifications" || view === "payments") return role === "resident" ? view : null;
  return (NAV[role] ?? RESIDENT_NAV).some((item) => item.id === view) ? view : null;
}

/** Puts the screen being drawn in the address bar, as a new entry or in place. */
function showAddress(view: string, entry: "push" | "replace"): void {
  const path = pathFor(view);
  if (window.location.pathname === path && window.location.search === "") return;
  if (entry === "push") {
    window.history.pushState({ view }, "", path);
  } else {
    window.history.replaceState({ view }, "", path);
  }
}

/** The screen a restored page was on, once, if it is one this role has. */
function resumedView(role: string): string | null {
  let view: string | null = null;
  try {
    view = sessionStorage.getItem(RESUME_KEY);
    sessionStorage.removeItem(RESUME_KEY);
  } catch (cause) {
    console.warn("Could not read the screen to resume", cause);
  }
  if (view === null) return null;
  if (view === "payments" || view === "notifications") return role === "resident" ? view : null;
  return destinations(role).some((item) => item.id === view) ? view : null;
}

/**
 * The chat's listeners run only while the chat is on screen and the tab is in
 * front of somebody: each one is a read for every message sent, and the room
 * is meant to cost nothing. A hidden tab keeps what it read, so coming back
 * is not a blank screen.
 */
function followRoom(): void {
  if (session && current === "chat" && document.visibilityState === "visible") {
    followChat(session);
  } else {
    followChat(null, current === "chat");
  }
}

function drawNav(): void {
  if (!session) {
    return;
  }
  const list = el("navList");
  const scrollLeft = list.scrollLeft;
  const focused = list.querySelector<HTMLButtonElement>(":focus")?.dataset.view;
  const selected = current.startsWith("analytics-") ? "home" : current;
  list.textContent = "";
  for (const item of destinations(session.role)) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `nav-item${item.id === selected ? " active" : ""}`;
    button.dataset.view = item.id;
    if (item.id === selected) button.setAttribute("aria-current", "page");

    // Each tab says itself twice, the page's language over the other one, the
    // way signs here are written.
    const main = document.createElement("span");
    main.className = "nav-main";
    main.textContent = t(item.label);
    const under = document.createElement("span");
    under.className = "nav-other";
    under.lang = otherLanguage();
    under.textContent = other(item.label);
    button.append(main, under);

    if (item.counted && store.waiting.length > 0) {
      const badge = document.createElement("span");
      badge.className = "badge";
      badge.textContent = String(store.waiting.length);
      button.append(badge);
    }

    button.addEventListener("click", () => {
      go(item.id);
    });
    list.append(button);
    if (focused === item.id) button.focus({ preventScroll: true });
  }
  list.scrollLeft = scrollLeft;
}

/**
 * The bell, for a resident: how many notifications they have not opened, and
 * the way into them. Staff are not sent any, so they get no bell to wonder at.
 */
function drawBell(): void {
  const bell = el("bell");
  if (!session || session.role !== "resident") {
    bell.classList.add("hidden");
    return;
  }
  bell.classList.remove("hidden");
  const unread = store.inbox.filter((item) => item.readAt === null).length;
  const count = el("bellCount");
  count.textContent = unread > 99 ? "99+" : String(unread);
  count.classList.toggle("hidden", unread === 0);
  bell.setAttribute(
    "aria-label",
    unread === 0
      ? t("inbox.title")
      : `${t("inbox.title")}, ${t("inbox.unread", { count: unread })}`,
  );
  bell.classList.toggle(
    "active",
    current === "notifications" || current.startsWith("notification:"),
  );
}

/**
 * The way into the chat, top right, with a red mark saying how many messages
 * came since this browser last had the chat open, "10+" past ten. For the two
 * roles in the chat; the count reads only the newest few messages.
 *
 * Having the chat open in front of somebody is what reads it, so while it is,
 * the mark is cleared and the time it was read up to moves forward.
 */
function drawChatButton(): void {
  const button = el<HTMLButtonElement>("chatButton");
  if (!session || !destinations(session.role).some((item) => item.id === "chat")) {
    button.classList.add("hidden");
    return;
  }
  button.classList.remove("hidden");
  const uid = session.user.uid;
  const looking = current === "chat" && document.visibilityState === "visible";
  if (looking) {
    markChatSeen(uid, Math.max(newestSent(store.chatRecent), newestSent(store.chat.messages)));
  }
  const count = looking ? 0 : unreadChat(store.chatRecent, chatSeenAt(uid), uid);
  const badge = el("chatCount");
  badge.textContent = unreadLabel(count);
  badge.classList.toggle("hidden", count === 0);
  button.setAttribute(
    "aria-label",
    count === 0
      ? t("nav.chat")
      : `${t("nav.chat")}, ${t("chat.unread", { count: unreadLabel(count) })}`,
  );
  button.classList.toggle("active", current === "chat");
}

function drawUser(): void {
  if (!session) {
    return;
  }
  const role = ROLE_NAME[session.role];
  el("userName").textContent = session.unitId ?? (role === null ? "" : t(role));
  el("userMeta").textContent = [
    session.unitId !== undefined && role !== null ? t(role) : null,
    store.orgName,
  ]
    .filter((part) => part !== null && part !== "")
    .join(" · ");
}

function viewFor(): View {
  if (!session) {
    throw new Error("no session");
  }
  if (session.role === "accountant") {
    return accountant(current, session);
  }
  if (session.role === "committee") {
    return committee(current, session);
  }
  return resident(current, session);
}

function draw(animate = true): void {
  if (!session) {
    return;
  }
  const content = el("content");
  const scrollTop = window.scrollY;
  const focused =
    document.activeElement instanceof HTMLElement
      ? {
          id: document.activeElement.id,
          view: document.activeElement.dataset.view,
          unit: document.activeElement.dataset.unit,
        }
      : null;

  /*
   * The sidebar is painted on every pass, including the ones that end in a
   * skeleton. It reads the org name and the size of the queue and nothing
   * else, so making it wait for the screen's own data is what left the
   * destinations unlabelled while a slow collection was still coming.
   */
  drawNav();
  drawUser();
  drawBell();
  followRoom();
  drawChatButton();
  drawPaid();

  if (store.problem) {
    el("pageTitle").textContent = t("common.problem");
    el("pageSub").textContent = "";
    content.textContent = store.problem.message;
    return;
  }

  const shell = shellFor(session.role, current);
  if (!has(...shell.needs)) {
    /*
     * The destination is already known, so the topbar says where you are
     * rather than "Loading" and then changing its mind. Only the figures are
     * missing, and the shape of them is on screen while they come.
     */
    const here = destinations(session.role).find((item) => item.id === current);
    title(here ? t(here.label) : t("common.loading"), here ? other(here.label) : "");
    el("pageSub").textContent = store.orgName;
    content.setAttribute("aria-busy", "true");
    content.innerHTML = skeleton(shell.shape());
    return;
  }

  const view = viewFor();
  const here = destinations(session.role).find((item) => item.id === current);
  title(view.title, here && t(here.label) === view.title ? other(here.label) : "");
  el("pageSub").textContent = view.sub === "" ? store.orgName : view.sub;

  content.removeAttribute("aria-busy");
  // A live update redraws the same screen: keep what the person was in the
  // middle of. A navigation draws a new one, which starts clean.
  const drafts = animate ? null : captureDrafts(content, document.activeElement);
  content.classList.remove("content-swap");
  if (animate) {
    void content.offsetWidth;
  }
  content.innerHTML = view.html;
  if (animate) {
    content.classList.add("content-swap");
  }
  if (drafts) {
    restoreDrafts(content, drafts);
  }
  view.wire?.(content, go);
  if (animate) {
    stagger(content);
  }
  if (!animate) {
    window.scrollTo({ top: scrollTop, behavior: "auto" });
    if (focused?.id) {
      document.getElementById(focused.id)?.focus({ preventScroll: true });
    } else if (focused?.view) {
      content.querySelector<HTMLElement>(`[data-view="${CSS.escape(focused.view)}"]`)?.focus({
        preventScroll: true,
      });
    } else if (focused?.unit) {
      content.querySelector<HTMLElement>(`[data-unit="${CSS.escape(focused.unit)}"]`)?.focus({
        preventScroll: true,
      });
    }
  }
}

/** The page's heading, with the same words in the other language under it. */
function title(main: string, under: string): void {
  const heading = el("pageTitle");
  heading.textContent = main;
  if (under !== "") {
    const line = document.createElement("span");
    line.className = "title-other";
    line.lang = otherLanguage();
    line.textContent = under;
    heading.append(line);
  }
}

function go(view: string): void {
  if (view === "chat" && current !== "chat") {
    openedChat();
  }
  current = view;
  showAddress(view, "push");
  draw();
  el("navList").querySelector(".active")?.scrollIntoView({ block: "nearest", inline: "nearest" });
}

function wire(): void {
  wireReceiptViewer();
  el("signOut").addEventListener("click", () => {
    const was = session;
    letGo();
    void signOut().then(
      () => {
        window.location.href = "/";
      },
      (cause: Error) => {
        console.error("Signing out failed", cause);
        // Still signed in, so carry on as before.
        if (was) {
          hold(was);
          draw();
        }
        toast(t("common.problem"));
      },
    );
  });
  document.addEventListener("visibilitychange", () => {
    followRoom();
    drawChatButton();
  });
  // Back and Forward move between screens the way they move between pages.
  window.addEventListener("popstate", (event) => {
    if (!session) return;
    const state: unknown = event.state;
    const saved =
      typeof state === "object" && state !== null && "view" in state
        ? String((state as { view: unknown }).view)
        : null;
    const view = saved ?? linkedView(session.role) ?? "home";
    if (view === "chat" && current !== "chat") {
      openedChat();
    }
    current = view;
    draw();
  });
  el("chatButton").addEventListener("click", () => {
    go("chat");
  });
  el("bell").addEventListener("click", () => {
    go("notifications");
  });
  el("langToggle").addEventListener("click", () => {
    setLanguage(language() === "am" ? "en" : "am");
    document.documentElement.lang = language();
    translate();
    refreshAppearance();
    draw();
  });
}

document.documentElement.lang = language();
translate();
wire();

// Carries on the logo from the sign-in form, or shows it if starting up is slow.
const loading = showLoaderIfSlow(t("common.loading"));

void currentSession().then(
  (found) => {
    if (!found) {
      // Nothing to draw and nothing to say. Straight to the form, which is
      // this app's front door: the marketing page is a separate site. The form
      // remembers the screen asked for and opens it once they are in.
      window.location.replace(signInPath(screenAt(window.location.pathname)));
      return;
    }
    session = found;
    void organizationStatus(found.orgId).then(
      (status) => {
        // The rules close a complex that is not live to its members, so every
        // read would fail one at a time. One sentence says why instead.
        if (status !== "active") {
          store.problem = new Error(t(status === "suspended" ? "app.suspended" : "app.notLiveYet"));
          loading.hide();
          draw();
          return;
        }
        current = resumedView(found.role) ?? linkedView(found.role) ?? current;
        // Back from Telebirr's checkout: open the pay screen on that order and
        // ask Telebirr now, rather than wait for its notification to arrive.
        const returned = new URLSearchParams(window.location.search).get("telebirr");
        if (returned && found.role === "resident") {
          current = "pay";
          followTelebirrOrder(returned);
          void checkTelebirrPayment(found.orgId, { orderId: returned }).catch((cause: Error) => {
            console.error("Checking a Telebirr order failed", cause);
          });
        }
        // The same for Chapa's page.
        const fromChapa = new URLSearchParams(window.location.search).get("chapa");
        if (fromChapa && found.role === "resident") {
          current = "pay";
          followChapaOrder(fromChapa);
          void checkChapaPayment(found.orgId, { orderId: fromChapa }).catch((cause: Error) => {
            console.error("Checking a Chapa order failed", cause);
          });
        } else if (found.role === "resident" && found.unitId && found.termId) {
          // Back by hand rather than through Chapa's link: ask Chapa about the
          // resident's own unfinished orders, now and whenever the tab comes
          // back into view, so a payment that went through is recorded anyway.
          const { orgId, unitId, termId } = found;
          const recheck = () => {
            void unfinishedChapaOrders(orgId, unitId, termId)
              .then((orders) =>
                Promise.all(
                  orders.map(async (orderId) => {
                    // Paid just now: follow it, so the resident is told at once.
                    const { status } = await checkChapaPayment(orgId, { orderId });
                    if (status === "paid") followChapaOrder(orderId);
                  }),
                ),
              )
              .catch((cause: Error) => {
                console.error("Checking unfinished Chapa orders failed", { orgId, unitId, cause });
              });
          };
          recheck();
          if (recheckOnView) document.removeEventListener("visibilitychange", recheckOnView);
          recheckOnView = () => {
            if (document.visibilityState === "visible") recheck();
          };
          document.addEventListener("visibilitychange", recheckOnView);
        }
        // The address says the screen being drawn, and loses the payment
        // provider's order number now it has been read.
        showAddress(current, "replace");
        // draw() paints the sidebar itself now, so the first pass puts the
        // destinations and the skeleton up together.
        draw();
        loading.hide();
        hold(found);
        // Signed out in another tab, or the account switched off: back to the
        // form, which opens this same screen again once they are in.
        onSignedOut(() => {
          if (session === null) return;
          letGo();
          window.location.replace(signInPath(screenAt(window.location.pathname)));
        });
      },
      (cause: Error) => {
        store.problem = cause;
        loading.hide();
        draw();
      },
    );
  },
  (cause: Error) => {
    store.problem = cause;
    loading.hide();
    draw();
  },
);
